import { ItemsPerPage, Table } from '@billy/design-system'
import Cookies from 'js-cookie'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Row } from 'react-table'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'
import { RecurringInvoicesSortProperty } from '@features/recurringInvoices/enums/recurringInvoicesSortProperty'
import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CookieKeys } from '../../../../../../enums/cookieKeys'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../enums/tableIds'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useRecurringInvoicesListFilters } from '../../context/recurringInvoicesListFilters'
import { useFetchRecurringInvoices } from '../../hooks/useFetchRecurringInvoices'
import { RecurringInvoicesTableRow } from '../../types/recurringInvoicesTableRow'
import { RecurringInvoicesTableEmptyRowInfo } from './elements/RecurringInvoicesTableEmptyRowInfo'
import { useRecurringInvoicesTableColumns } from './hooks/useRecurringInvoicesTableColumns'
import { getRecurringInvoicesTableData } from './utils/getRecurringInvoicesTableData'

export const RecurringInvoicesTable = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const { organization } = useUserOrganization()

  const { columns } = useRecurringInvoicesTableColumns()
  const { recurringInvoices, isFetching, isLoading, pagination } = useFetchRecurringInvoices()
  const [filters, setFilters] = useRecurringInvoicesListFilters()

  const { pageSize, sortProperty, sortDirection } = filters

  const recurringInvoicesData = useMemo(
    () => getRecurringInvoicesTableData(recurringInvoices, t),
    [recurringInvoices, t],
  )

  const sortedColumnId = useMemo(
    () => getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        page: 1,
        sortProperty: columnId as RecurringInvoicesSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({
        page,
      })
    },
    [setFilters],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({
        page: 1,
        pageSize: itemsPerPage,
      })
    },
    [setFilters],
  )

  const handleRowClick = useCallback(
    (row: Row<RecurringInvoicesTableRow>) => {
      const { id: recurringInvoiceId, status } = row.original

      Cookies.set(CookieKeys.InvoicingRecurringInvoicesListQuery, location.search)

      const route =
        status === RecurringInvoiceStatus.Draft
          ? reactRoute.organization.invoices.getEditDocumentDraftRoute(
              getInvoiceDocumentRouteBase(InvoiceDocumentType.RecurringInvoice),
              recurringInvoiceId,
            )
          : reactRoute.organization.recurringInvoices.getViewRecurringInvoiceRoute(recurringInvoiceId)

      navigate(getOrganizationViewUrl(organization?.url, route))
    },
    [navigate, location.search, organization?.url],
  )

  return (
    <Table
      columns={columns}
      currentPage={pagination?.page}
      data={recurringInvoicesData}
      emptyRowInfo={<RecurringInvoicesTableEmptyRowInfo />}
      id={TableIds.RecurringInvoicesList}
      isLoading={isLoading || isFetching}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
